import React, { useRef, useState } from 'react';
import { Barcode, Camera, Loader2 } from 'lucide-react';
import { BarcodeScanner } from './BarcodeScanner';
import api from '../api';

/**
 * Drop-in barcode scan input for any page that selects/enters a product.
 *
 *   <BarcodeScanInput onScan={(product) => addProduct(product)} />
 *
 * Behavior:
 *   • Text input that auto-submits on rapid input (USB scanner) or Enter.
 *   • Camera icon button opens the existing `BarcodeScanner` modal.
 *   • Looks up by barcode OR SKU via `/api/barcode/{code}`.
 *   • On success → fires `onScan(product)` with the resolved product payload
 *     (`{ sku, product_name, variant_id, product_id, unit_price, ... }`).
 *   • On miss → flashes a red border, calls optional `onMiss(code)`.
 *
 * Props:
 *   onScan       (product) => void  — called when a product is resolved
 *   onMiss       (code)    => void  — called when the code is not found
 *   placeholder  string             — input placeholder
 *   autoFocus    bool               — focus on mount (default true)
 *   className    string             — wrapper class
 */
export default function BarcodeScanInput({
  onScan,
  onMiss,
  placeholder = 'Scan barcode or type SKU…',
  autoFocus = true,
  className = '',
}) {
  const [code, setCode] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(false);
  const [showCamera, setShowCamera] = useState(false);
  const inputRef = useRef(null);
  const timerRef = useRef(null);
  const lastInputAt = useRef(0);

  const lookup = async (raw) => {
    const value = (raw || '').trim();
    if (!value) return;
    setBusy(true);
    setError(false);
    try {
      const product = await api.lookupBarcode(value);
      if (product) {
        setCode('');
        onScan && onScan(product);
        inputRef.current?.focus();
      } else {
        setError(true);
        onMiss && onMiss(value);
      }
    } catch {
      setError(true);
      onMiss && onMiss(value);
    } finally {
      setBusy(false);
      setTimeout(() => setError(false), 1500);
    }
  };

  const handleChange = (e) => {
    const val = e.target.value;
    setCode(val);
    setError(false);
    clearTimeout(timerRef.current);
    // Heuristic: USB scanners write the whole code in <50ms then send Enter.
    // We auto-submit after 220ms of no typing to support BOTH scanners and humans.
    const now = Date.now();
    const fastScan = val.length >= 4 && (now - lastInputAt.current) < 50 && /\n|\r/.test(val);
    lastInputAt.current = now;
    if (fastScan || val.length >= 5) {
      timerRef.current = setTimeout(() => lookup(val.replace(/[\r\n]/g, '')), 220);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      clearTimeout(timerRef.current);
      lookup(code);
    }
  };

  const handleCameraScan = (decoded) => {
    setShowCamera(false);
    lookup(decoded);
  };

  return (
    <>
      <div className={`flex items-center gap-2 ${className}`} data-testid="barcode-scan-input">
        <div className={`flex items-center gap-2 flex-1 px-3 py-2 rounded-md border ${error ? 'border-red-500 bg-red-50' : 'border-border bg-background'} focus-within:ring-2 focus-within:ring-primary/30`}>
          <Barcode className={`h-4 w-4 ${error ? 'text-red-500' : 'text-muted-foreground'}`} />
          <input
            ref={inputRef}
            value={code}
            onChange={handleChange}
            onKeyDown={handleKeyDown}
            placeholder={placeholder}
            autoFocus={autoFocus}
            className="flex-1 bg-transparent outline-none text-sm"
            data-testid="barcode-scan-text-input"
          />
          {busy && <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />}
        </div>
        <button
          type="button"
          onClick={() => setShowCamera(true)}
          className="flex items-center justify-center px-3 py-2 rounded-md border border-border hover:bg-muted"
          title="Open camera scanner"
          data-testid="barcode-scan-camera-btn"
        >
          <Camera className="h-4 w-4" />
        </button>
      </div>
      {showCamera && (
        <BarcodeScanner
          onScan={handleCameraScan}
          onClose={() => setShowCamera(false)}
        />
      )}
    </>
  );
}
